import { cn } from "@/lib/utils";
import { useMsAdlCopy } from "@/features/i18n/msAdlCopy";
import type { BaseEntryValueProps } from "./BaseEntryValue";
import { BaseEntryValue } from "./BaseEntryValue";

export interface RatingsListEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    value: Record<string, number> | null;
    showAverage?: boolean;
    /** When true, map known MS-ADL activity keys to the active locale. */
    localiseMsAdlActivities?: boolean;
}

export function RatingsListEntryValue({
    value,
    showAverage = true,
    size,
    className,
    localiseMsAdlActivities = false,
    ...props
}: RatingsListEntryValueProps) {
    const { formatActivity } = useMsAdlCopy();

    if (!value || Object.keys(value).length === 0) {
        return (
            <BaseEntryValue size={size} className={className} {...props}>
                <span
                    className={cn(
                        "text-slate-500 dark:text-slate-400",
                        size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-base"
                    )}
                >
                    No activities recorded
                </span>
            </BaseEntryValue>
        );
    }

    const activities = Object.entries(value);
    const averageScore = showAverage
        ? Math.round((Object.values(value).reduce((sum, score) => sum + score, 0) / activities.length) * 10) / 10
        : null;

    return (
        <BaseEntryValue size={size} className="p-1" {...props}>
            <div className="space-y-2">
                {/* Activities Table */}
                <div className="space-y-1">
                    {activities.map(([activity, score]) => (
                        <div key={activity} className="flex justify-between items-center gap-3">
                            <span
                                className={cn(
                                    "text-slate-700 dark:text-slate-200 truncate",
                                    size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm"
                                )}
                            >
                                {localiseMsAdlActivities ? formatActivity(activity) : activity}
                            </span>
                            <div className="flex items-center gap-2">
                                {/* Score Badge */}
                                <div
                                    className={cn(
                                        "flex justify-center items-center bg-white/80 rounded-md font-medium",
                                        size === "sm"
                                            ? "text-xs w-8 h-6"
                                            : size === "lg"
                                              ? "text-base w-10 h-8"
                                              : "text-sm w-9 h-7"
                                    )}
                                >
                                    {score}
                                </div>
                                {/* Mini Slider Visual */}
                                <div className="flex items-center gap-1">
                                    <div className="bg-slate-200 rounded-full w-12 h-1 overflow-hidden">
                                        <div
                                            className="bg-blue-500 rounded-full h-full transition-all"
                                            style={{
                                                width: `${(score / 10) * 100}%`,
                                            }}
                                        />
                                    </div>
                                    <span className="text-slate-500 text-xs">/10</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Average Score */}
                {showAverage && averageScore !== null && (
                    <div className="pt-2 border-slate-200 border-t">
                        <div className="flex justify-between items-center">
                            <span
                                className={cn(
                                    "font-medium text-slate-700 dark:text-slate-200",
                                    size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm"
                                )}
                            >
                                Average Score
                            </span>
                            <div
                                className={cn(
                                    "flex justify-center items-center bg-blue-50 px-3 py-1 border border-blue-300 rounded-md font-bold text-blue-700",
                                    size === "sm"
                                        ? "text-sm w-12 h-6"
                                        : size === "lg"
                                          ? "text-lg w-16 h-8"
                                          : "text-base w-14 h-7"
                                )}
                            >
                                {averageScore}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </BaseEntryValue>
    );
}
